import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { SwalService } from './../../services/swal.service';

interface EstructuraInput {
  type: string;
  title: string;
  maxlength?: number;
  required?: boolean;
  placeholder?: string;
  accept?: string;
  maxSize?: string;
  min?: number | string;
  max?: number | string;
  rows?: number;
  options?: Array<{ value: string; label: string }>;
}

interface AnexoCargo {
  input_id: number;
  estructura_input: EstructuraInput;
}

@Component({
  selector: 'app-structure-create',
  templateUrl: './structure-create.component.html',
  styleUrls: ['./structure-create.component.css']
})
export class StructureCreateComponent implements OnInit {

  @Output() structuresChanged = new EventEmitter<AnexoCargo[]>();

  // Lista de estructuras creadas
  structures: AnexoCargo[] = [];

  // Tipos de inputs disponibles
  tiposInput = [
    { value: 'input-text', label: 'Texto', icon: 'fa-font' },
    { value: 'textarea', label: 'Área de texto', icon: 'fa-align-left' },
    { value: 'number', label: 'Número', icon: 'fa-hashtag' },
    { value: 'email', label: 'Email', icon: 'fa-envelope' },
    { value: 'tel', label: 'Teléfono', icon: 'fa-phone' },
    { value: 'date', label: 'Fecha', icon: 'fa-calendar' },
    { value: 'file', label: 'Archivo', icon: 'fa-file-upload' },
    { value: 'select', label: 'Selector', icon: 'fa-list' },
    { value: 'checkbox', label: 'Casillas múltiples', icon: 'fa-check-square' },
    { value: 'radio', label: 'Opción única', icon: 'fa-dot-circle' }
  ];

  // Tipos de archivos disponibles
  tiposArchivo = [
    { value: '.pdf', label: 'PDF (.pdf)' },
    { value: '.docx', label: 'Word (.docx)' },
    { value: '.xlsx', label: 'Excel (.xlsx)' },
    { value: '.jpg', label: 'JPG (.jpg)' },
    { value: '.jpeg', label: 'JPEG (.jpeg)' },
    { value: '.png', label: 'PNG (.png)' }
  ];

  // Formulario para nuevo campo
  nuevoCampo: EstructuraInput = {
    type: 'input-text',
    title: '',
    required: true
  };

  // Para agregar opciones a select/checkbox/radio
  nuevaOpcion = {
    value: '',
    label: ''
  };

  // Estado del formulario
  mostrarFormulario = false;
  modoEdicion = false;
  indiceEdicion: number = null;

  constructor(private swalService: SwalService) { }

  ngOnInit() {
  }

  // ==================== MÉTODOS PRINCIPALES ====================

  abrirFormularioNuevoCampo() {
    this.resetearFormulario();
    this.mostrarFormulario = true;
    this.modoEdicion = false;
  }

  resetearFormulario() {
    this.nuevoCampo = {
      type: 'input-text',
      title: '',
      required: true,
      maxlength: 90,
      placeholder: ''
    };
    this.nuevaOpcion = { value: '', label: '' };
  }

  cambiarTipoCampo() {
    // Guardar valores base
    const title = this.nuevoCampo.title;
    const required = this.nuevoCampo.required;

    // Limpiar y establecer propiedades según el tipo
    switch (this.nuevoCampo.type) {
      case 'input-text':
      case 'email':
      case 'tel':
        this.nuevoCampo = {
          type: this.nuevoCampo.type,
          title: title,
          required: required,
          maxlength: 90,
          placeholder: ''
        };
        break;

      case 'textarea':
        this.nuevoCampo = {
          type: 'textarea',
          title: title,
          required: required,
          maxlength: 500,
          rows: 4,
          placeholder: ''
        };
        break;

      case 'number':
        this.nuevoCampo = {
          type: 'number',
          title: title,
          required: required,
          min: 0,
          max: 999999,
          placeholder: ''
        };
        break;

      case 'date':
        this.nuevoCampo = {
          type: 'date',
          title: title,
          required: required,
          min: '',
          max: ''
        };
        break;

      case 'file':
        this.nuevoCampo = {
          type: 'file',
          title: title,
          required: required,
          accept: '.pdf',
          maxSize: '5MB'
        };
        break;

      case 'select':
      case 'checkbox':
      case 'radio':
        this.nuevoCampo = {
          type: this.nuevoCampo.type,
          title: title,
          required: required,
          options: []
        };
        break;

      default:
        this.nuevoCampo = {
          type: this.nuevoCampo.type,
          title: title,
          required: required
        };
    }
  }

  agregarOpcion() {
    // Validar que los campos no estén vacíos
    if (!this.nuevaOpcion.value || !this.nuevaOpcion.value.trim()) {
      this.swalService.showWarningNotification('Advertencia', 'El valor de la opción es requerido');
      return;
    }

    if (!this.nuevaOpcion.label || !this.nuevaOpcion.label.trim()) {
      this.swalService.showWarningNotification('Advertencia', 'La etiqueta de la opción es requerida');
      return;
    }

    // Inicializar options si no existe
    if (!this.nuevoCampo.options) {
      this.nuevoCampo.options = [];
    }

    // Validar que no exista el mismo valor
    const valorExiste = this.nuevoCampo.options.find(
      opt => opt.value.toLowerCase() === this.nuevaOpcion.value.trim().toLowerCase()
    );

    if (valorExiste) {
      this.swalService.showWarningNotification('Advertencia', 'Ya existe una opción con ese valor');
      return;
    }

    // Validar que no exista la misma etiqueta
    const labelExiste = this.nuevoCampo.options.find(
      opt => opt.label.toLowerCase() === this.nuevaOpcion.label.trim().toLowerCase()
    );

    if (labelExiste) {
      this.swalService.showWarningNotification('Advertencia', 'Ya existe una opción con esa etiqueta');
      return;
    }

    // Agregar la opción
    this.nuevoCampo.options.push({
      value: this.nuevaOpcion.value.trim(),
      label: this.nuevaOpcion.label.trim()
    });

    // Limpiar campos
    this.nuevaOpcion = { value: '', label: '' };
    this.swalService.showSuccessNotification('Agregado', 'Opción agregada correctamente');
  }

  eliminarOpcion(index: number) {
    if (this.nuevoCampo.options && index >= 0 && index < this.nuevoCampo.options.length) {
      this.nuevoCampo.options.splice(index, 1);
      this.swalService.showSuccessNotification('Eliminado', 'Opción eliminada');
    }
  }

  validarCampo(): boolean {
    // Validar título
    if (!this.nuevoCampo.title || !this.nuevoCampo.title.trim()) {
      this.swalService.showWarningNotification('Advertencia', 'El título del campo es requerido');
      return false;
    }

    if (this.nuevoCampo.title.trim().length < 3) {
      this.swalService.showWarningNotification('Advertencia', 'El título debe tener al menos 3 caracteres');
      return false;
    }

    // Validaciones específicas por tipo
    switch (this.nuevoCampo.type) {
      case 'input-text':
      case 'email':
      case 'tel':
      case 'textarea':
        if (this.nuevoCampo.maxlength && this.nuevoCampo.maxlength < 1) {
          this.swalService.showWarningNotification('Advertencia', 'La longitud máxima debe ser mayor a 0');
          return false;
        }
        if (this.nuevoCampo.maxlength && this.nuevoCampo.maxlength > 5000) {
          this.swalService.showWarningNotification('Advertencia', 'La longitud máxima no puede ser mayor a 5000');
          return false;
        }
        break;

      case 'number':
        if (this.nuevoCampo.min !== undefined && this.nuevoCampo.max !== undefined) {
          const min = typeof this.nuevoCampo.min === 'string' ? parseFloat(this.nuevoCampo.min) : this.nuevoCampo.min;
          const max = typeof this.nuevoCampo.max === 'string' ? parseFloat(this.nuevoCampo.max) : this.nuevoCampo.max;

          if (!isNaN(min) && !isNaN(max) && min > max) {
            this.swalService.showWarningNotification('Advertencia', 'El valor mínimo no puede ser mayor al máximo');
            return false;
          }
        }
        break;

      case 'date':
        if (this.nuevoCampo.min && this.nuevoCampo.max) {
          const minDate = new Date(this.nuevoCampo.min as string);
          const maxDate = new Date(this.nuevoCampo.max as string);

          if (minDate > maxDate) {
            this.swalService.showWarningNotification('Advertencia', 'La fecha mínima no puede ser mayor a la máxima');
            return false;
          }
        }
        break;

      case 'file':
        if (!this.nuevoCampo.accept || !this.nuevoCampo.accept.trim()) {
          this.swalService.showWarningNotification('Advertencia', 'Debe especificar el tipo de archivo aceptado');
          return false;
        }

        if (!this.nuevoCampo.maxSize || !this.nuevoCampo.maxSize.trim()) {
          this.swalService.showWarningNotification('Advertencia', 'Debe especificar el tamaño máximo del archivo');
          return false;
        }

        // Validar formato de maxSize (debe ser número + MB o KB)
        const sizeRegex = /^\d+(\.\d+)?(MB|KB|GB)$/i;
        if (!sizeRegex.test(this.nuevoCampo.maxSize.trim())) {
          this.swalService.showWarningNotification(
            'Advertencia',
            'El formato del tamaño debe ser: número + MB/KB/GB (ejemplo: 5MB, 500KB)'
          );
          return false;
        }
        break;

      case 'textarea':
        if (this.nuevoCampo.rows && (this.nuevoCampo.rows < 2 || this.nuevoCampo.rows > 20)) {
          this.swalService.showWarningNotification('Advertencia', 'El número de filas debe estar entre 2 y 20');
          return false;
        }
        break;

      case 'select':
      case 'checkbox':
      case 'radio':
        if (!this.nuevoCampo.options || this.nuevoCampo.options.length === 0) {
          this.swalService.showWarningNotification('Advertencia', 'Debe agregar al menos una opción');
          return false;
        }

        if (this.nuevoCampo.options.length < 2) {
          this.swalService.showWarningNotification('Advertencia', 'Debe agregar al menos 2 opciones');
          return false;
        }
        break;
    }

    return true;
  }

  agregarCampo() {
    if (!this.validarCampo()) return;

    if (this.modoEdicion) {
      // Actualizar campo existente
      this.structures[this.indiceEdicion].estructura_input = this.limpiarCampo({ ...this.nuevoCampo });
      this.swalService.showSuccessNotification('Actualizado', 'Campo actualizado correctamente');
    } else {
      // Agregar nuevo campo
      const nuevaEstructura: AnexoCargo = {
        input_id: this.structures.length + 1,
        estructura_input: this.limpiarCampo({ ...this.nuevoCampo })
      };

      this.structures.push(nuevaEstructura);
      this.swalService.showSuccessNotification('Agregado', 'Campo agregado correctamente');
    }

    this.emitirCambios();
    this.cancelarFormulario();
  }

  limpiarCampo(campo: EstructuraInput): EstructuraInput {
    // Crear objeto base
    const campoLimpio: any = {
      type: campo.type,
      title: campo.title.trim(),
      required: campo.required
    };

    // Agregar propiedades específicas según el tipo
    switch (campo.type) {
      case 'input-text':
      case 'email':
      case 'tel':
        if (campo.maxlength && campo.maxlength > 0) {
          campoLimpio.maxlength = campo.maxlength;
        }
        if (campo.placeholder && campo.placeholder.trim()) {
          campoLimpio.placeholder = campo.placeholder.trim();
        }
        break;

      case 'textarea':
        if (campo.maxlength && campo.maxlength > 0) {
          campoLimpio.maxlength = campo.maxlength;
        }
        if (campo.rows && campo.rows > 0) {
          campoLimpio.rows = campo.rows;
        }
        if (campo.placeholder && campo.placeholder.trim()) {
          campoLimpio.placeholder = campo.placeholder.trim();
        }
        break;

      case 'number':
        if (campo.min !== undefined && campo.min !== '') {
          campoLimpio.min = typeof campo.min === 'string' ? parseFloat(campo.min) : campo.min;
        }
        if (campo.max !== undefined && campo.max !== '') {
          campoLimpio.max = typeof campo.max === 'string' ? parseFloat(campo.max) : campo.max;
        }
        if (campo.placeholder && campo.placeholder.trim()) {
          campoLimpio.placeholder = campo.placeholder.trim();
        }
        break;

      case 'date':
        if (campo.min && campo.min !== '') {
          campoLimpio.min = campo.min;
        }
        if (campo.max && campo.max !== '') {
          campoLimpio.max = campo.max;
        }
        break;

      case 'file':
        if (campo.accept && campo.accept.trim()) {
          campoLimpio.accept = campo.accept.trim();
        }
        if (campo.maxSize && campo.maxSize.trim()) {
          campoLimpio.maxSize = campo.maxSize.trim();
        }
        break;

      case 'select':
      case 'checkbox':
      case 'radio':
        if (campo.options && campo.options.length > 0) {
          campoLimpio.options = campo.options.map(opt => ({
            value: opt.value.trim(),
            label: opt.label.trim()
          }));
        }
        break;
    }

    return campoLimpio;
  }

  editarCampo(index: number) {
    if (index < 0 || index >= this.structures.length) {
      this.swalService.showErrorNotification('Error', 'Índice inválido');
      return;
    }

    this.indiceEdicion = index;
    this.modoEdicion = true;
    // Hacer una copia profunda para evitar modificar el original
    this.nuevoCampo = JSON.parse(JSON.stringify(this.structures[index].estructura_input));
    this.mostrarFormulario = true;
  }

  eliminarCampo(index: number) {
    if (index < 0 || index >= this.structures.length) {
      this.swalService.showErrorNotification('Error', 'Índice inválido');
      return;
    }

    this.swalService.showStyledConfirmationModal(
      '¿Está seguro?',
      'Esta acción eliminará el campo de forma permanente',
      'Sí, eliminar',
      'Cancelar'
    ).then((result) => {
      if (result.value) {
        this.structures.splice(index, 1);
        // Reordenar input_ids
        this.structures.forEach((estructura, idx) => {
          estructura.input_id = idx + 1;
        });
        this.emitirCambios();
        this.swalService.showSuccessNotification('Eliminado', 'Campo eliminado correctamente');
      }
    });
  }

  cancelarFormulario() {
    this.mostrarFormulario = false;
    this.modoEdicion = false;
    this.indiceEdicion = null;
    this.resetearFormulario();
  }

  emitirCambios() {
    // Emitir una copia del array
    this.structuresChanged.emit(JSON.parse(JSON.stringify(this.structures)));
  }

  moverCampo(index: number, direccion: 'arriba' | 'abajo') {
    if (index < 0 || index >= this.structures.length) {
      return;
    }

    if (direccion === 'arriba' && index > 0) {
      // Intercambiar con el elemento anterior
      const temp = this.structures[index];
      this.structures[index] = this.structures[index - 1];
      this.structures[index - 1] = temp;
    } else if (direccion === 'abajo' && index < this.structures.length - 1) {
      // Intercambiar con el elemento siguiente
      const temp = this.structures[index];
      this.structures[index] = this.structures[index + 1];
      this.structures[index + 1] = temp;
    } else {
      return; // No se puede mover
    }

    // Reordenar input_ids
    this.structures.forEach((estructura, idx) => {
      estructura.input_id = idx + 1;
    });

    this.emitirCambios();
  }

  obtenerIconoTipo(type: string): string {
    const tipo = this.tiposInput.find(t => t.value === type);
    return tipo ? tipo.icon : 'fa-question';
  }

  obtenerNombreTipo(type: string): string {
    const tipo = this.tiposInput.find(t => t.value === type);
    return tipo ? tipo.label : type;
  }

  // ==================== MÉTODOS PÚBLICOS ====================

  // Método público para obtener las estructuras
  getStructures(): AnexoCargo[] {
    return JSON.parse(JSON.stringify(this.structures));
  }

  // Método público para limpiar todas las estructuras
  clearStructures() {
    if (this.structures.length === 0) {
      this.swalService.showWarningNotification('Advertencia', 'No hay campos para limpiar');
      return;
    }

    this.swalService.showStyledConfirmationModal(
      '¿Está seguro?',
      'Se eliminarán todos los campos creados',
      'Sí, limpiar todo',
      'Cancelar'
    ).then((result) => {
      if (result.value) {
        this.structures = [];
        this.emitirCambios();
        this.swalService.showSuccessNotification('Limpiado', 'Todos los campos han sido eliminados');
      }
    });
  }

  // Método público para cargar estructuras existentes
  loadStructures(structures: AnexoCargo[]) {
    if (!structures || !Array.isArray(structures)) {
      this.swalService.showErrorNotification('Error', 'El formato de las estructuras no es válido');
      return;
    }

    this.structures = JSON.parse(JSON.stringify(structures));
    this.emitirCambios();
  }

  // Método para verificar si un tipo requiere opciones
  tipoRequiereOpciones(type: string): boolean {
    return ['select', 'checkbox', 'radio'].includes(type);
  }
}
